import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LoadingComponent from "../../../components/LoadingComponent/LoadingComponent.tsx";
import NavbarV3 from "../../../components/NavbarV3/NavbarV3.tsx";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import { useToast } from "../../../contexts/ToastContext.tsx";
import CadastroEditHeader from "../../../core/components/CadastroEditHeader.tsx";
import MotionSemAutorizacao from "../../../core/components/MotionSemAutorizacao.tsx";
import useService from "../../../hooks/useService.ts";
import { Embarque } from "../../../models/Embarque.ts";
import { getEmbarquePorId } from "../../../services/ModuloGerenciador/Embarque.js";
import { getTipoContatoPorProprietarioId } from "../../../services/ModuloGerenciador/TipoContato.js";
import CadastroViagem from "./StepperV2/CadastroViagem.js";

const colunasIgnorar = [
  "idEmbarque",
  "nEntregas",
  "nEntregasFeitas",
  "pedido",
  "km",
  "usuarioResponsavelId",
  "embarqueHistoricoId",
  "load",
  "cte",
  "peso",
];

export function EditarEmbarque() {
  const { profile, contextoEmbarque, hasRegra, dadosFilial } = useProfile();
  const navigate = useNavigate();
  const params = useParams();
  const { showToast } = useToast();

  const buscouViagem = useRef(false);

  const [values, setValues] = useState<Embarque>();
  const [dadoMotorista, setDadoMotorista] = useState(null);
  const [dadoContato, setDadoContato] = useState(null);
  const [semAutorizacao, setSemAutorizacao] = useState(false);

  const formatarValores = useCallback((resultData: any) => {
    const ehInteger = (value: any) => {
      return (
        (typeof value === "number" && Math.floor(value) === value) ||
        (typeof value === "string" && /^\d+$/.test(value))
      );
    };
    Object.keys(resultData).forEach((key) => {
      if (colunasIgnorar.includes(key)) {
        return;
      }
      const value = resultData[key];

      if (key === "kmVazio" && typeof value === "string") {
        resultData[key] = Number(value.replace(",", "."));
      }

      if (ehInteger(value) && key !== "kmVazio") {
        resultData[key] = `${value},00`;
      }
    });

    return resultData;
  }, []);

  const buscarTipoContato = async (value: any) => {
    try {
      await getTipoContatoPorProprietarioId(value, profile?.token).then(
        (result) => {
          setDadoContato(result.data);
        }
      );
    } catch (error) {
      console.error(error);
    }
  };

  const [buscandoEmbarquePorId, buscarEmbarquePorId] = useService(
    ({ id, token }: { id: number; token: string }) =>
      getEmbarquePorId({ id, token }),
    {
      autoStart: false,
      onData: (result) => {
        if (result.status === 200) {
          const formattedData = formatarValores(result.data);
          if (contextoEmbarque === "frota" && formattedData.contexto !== "") {
            showToast(
              "Essa viagem só pode ser editada na tela que foi cadastrada.",
              "error"
            );
            navigate(`/viagens/${contextoEmbarque}`);
          }
          if (!!formattedData.veiculo && !!formattedData.veiculoMotorista) {
            setDadoMotorista(formattedData.veiculoMotorista);
            buscarTipoContato(
              formattedData.veiculo.proprietarioVeiculo?.idProprietario
            );
          }
          setValues(formattedData);
        }
      },
      onError: (error) => {
        console.error(error);
        navigate("/viagens/" + contextoEmbarque);
      },
    }
  );

  const [buscandoEmbarqueOrigemPorId, buscarEmbarqueOrigemPorId] = useService(
    ({ id, token }: { id: number; token: string }) =>
      getEmbarquePorId({ id, token }),
    {
      autoStart: false,
      onData: (result) => {
        if (result.status === 200) {
          const formattedData = formatarValores(result.data);
          if (!!formattedData.veiculo && !!formattedData.veiculoMotorista) {
            setDadoMotorista(formattedData.veiculoMotorista);
            buscarTipoContato(
              formattedData.veiculo.proprietarioVeiculo?.idProprietario
            );
          }
          setValues(formattedData);
        }
      },
      onError: (error) => {
        console.error(error);
        navigate("/viagens/" + contextoEmbarque);
      },
    }
  );

  useEffect(() => {
    if (params.id && profile?.token && !buscouViagem.current) {
      if (params?.origem === "origem") {
        buscarEmbarqueOrigemPorId({
          id: Number(params.id),
          token: profile.token,
        });
      } else {
        buscarEmbarquePorId({
          id: Number(params.id),
          token: profile.token,
        });
      }

      setSemAutorizacao(
        !hasRegra(
          dadosFilial?.find((item) => item.contexto === contextoEmbarque)
            ?.nomeRegra ?? ""
        )
      );
      buscouViagem.current = true;
    }
  }, [params.id, profile?.token, hasRegra, contextoEmbarque, dadosFilial, params?.origem, buscarEmbarqueOrigemPorId, buscarEmbarquePorId]);

  const isLoading = useMemo(() => {
    return (
      buscandoEmbarquePorId ||
      buscandoEmbarqueOrigemPorId ||
      !profile?.token ||
      !params.id ||
      !values
    );
  }, [
    buscandoEmbarquePorId,
    buscandoEmbarqueOrigemPorId,
    profile,
    params,
    values,
  ]);

  const contextoRota = useMemo(() => {
    return params?.contexto;
  }, [params]);

  const contextoFilial = useMemo(() => {
    return dadosFilial?.find((filial) => filial.contexto === contextoRota)
      ?.nomeFilial;
  }, [dadosFilial, contextoRota]);

  if (semAutorizacao) {
    return <MotionSemAutorizacao />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="layout-container"
    >
      <NavbarV3 title={`Editar Embarque - ${contextoFilial}`} />
      <CadastroEditHeader
        dadosTitle="Dados de Viagens"
        headerTitle="Editar Embarque"
        onClick={() => navigate(-1)}
      />
      {isLoading && <LoadingComponent />}
      {!isLoading && (
        <CadastroViagem
          dadoMotorista={dadoMotorista}
          dadoContato={dadoContato}
          dados={values}
          editando
        />
      )}
    </motion.div>
  );
}
